export default function RiskGauges({ risk }) {
  if (!risk) {
    return (
      <div className="text-xs text-[var(--aurion-muted)]" data-testid="risk-gauges-empty">
        …
      </div>
    );
  }
  const heat = Number(risk.portfolio_heat_pct || 0);
  const dd = Number(risk.drawdown?.drawdown_pct || 0);
  const kill = Number(risk.kill_threshold_pct || 5);
  const conc = Number(risk.concentration_pct || 0);
  const armed = dd < kill;

  return (
    <div data-testid="risk-gauges" className="space-y-3">
      <div className="flex items-baseline justify-between">
        <div className="aurion-section-title">Risk Gate</div>
        <div
          data-testid="risk-gate-status"
          className={`font-mono text-[10px] tracking-widest ${
            armed ? "text-[var(--aurion-green)]" : "text-[var(--aurion-red)]"
          }`}
        >
          {armed ? "ARMED" : "KILL BREACHED"}
        </div>
      </div>

      <Gauge
        label="Portfolio Heat"
        value={heat}
        suffix="%"
        color="var(--aurion-gold)"
        testid="gauge-heat"
      />
      <DrawdownGauge dd={dd} kill={kill} />
      <Gauge
        label="Concentration"
        value={conc}
        suffix={risk.concentration_symbol ? `% · ${risk.concentration_symbol}` : "%"}
        color="var(--aurion-lavender)"
        testid="gauge-concentration"
      />
      <div className="flex items-baseline justify-between border-t border-[var(--aurion-border)] pt-2">
        <div className="aurion-section-title">Gross Exposure</div>
        <div
          className="font-mono text-sm text-[var(--aurion-text)]"
          data-testid="gauge-exposure"
        >
          {fmtUsd(risk.gross_exposure)}
        </div>
      </div>
    </div>
  );
}

function Gauge({ label, value, suffix, color, testid }) {
  const pct = Math.max(0, Math.min(100, value));
  return (
    <div data-testid={testid}>
      <div className="flex items-baseline justify-between">
        <div className="aurion-section-title">{label}</div>
        <div className="font-mono text-sm text-[var(--aurion-text)]">
          <span data-testid={`${testid}-value`}>{value.toFixed(2)}</span>
          {suffix}
        </div>
      </div>
      <div className="mt-1 h-1.5 w-full overflow-hidden bg-[var(--aurion-panel-3)]">
        <div
          style={{ width: `${pct}%`, background: color }}
          className="h-full transition-all duration-500 ease-out"
        />
      </div>
    </div>
  );
}

function DrawdownGauge({ dd, kill }) {
  // Bar caps at 2×kill so the marker sits at the middle of the bar.
  const cap = Math.max(kill * 2, 10);
  const ddPct = Math.max(0, Math.min(cap, dd));
  const killPct = (kill / cap) * 100;
  const breach = dd >= kill;
  return (
    <div data-testid="gauge-drawdown">
      <div className="flex items-baseline justify-between">
        <div className="aurion-section-title">Daily Drawdown</div>
        <div
          className={`font-mono text-sm ${
            breach ? "text-[var(--aurion-red)]" : "text-[var(--aurion-text)]"
          }`}
          data-testid="gauge-drawdown-value"
        >
          {dd.toFixed(2)}%
        </div>
      </div>
      <div className="relative mt-1 h-1.5 w-full overflow-visible bg-[var(--aurion-panel-3)]">
        <div
          style={{
            width: `${(ddPct / cap) * 100}%`,
            background: breach ? "var(--aurion-red)" : "var(--aurion-amber)",
          }}
          className="h-full transition-all duration-500 ease-out"
        />
        <div style={{ left: `${killPct}%` }} className="kill-line" />
      </div>
      <div className="mt-1 flex justify-between text-[9px] uppercase tracking-widest text-[var(--aurion-muted)]">
        <span>0%</span>
        <span>KILL {kill}%</span>
      </div>
    </div>
  );
}

function fmtUsd(n) {
  const v = Number(n || 0);
  if (Math.abs(v) >= 1_000_000) return `$${(v / 1_000_000).toFixed(2)}M`;
  if (Math.abs(v) >= 1_000) return `$${(v / 1_000).toFixed(1)}K`;
  return `$${v.toFixed(2)}`;
}
